import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import BookTimeFrameService from "@app/services/http/book-time-frame.service";
import { Meta } from "@app/types";
import { BookTimeFrame } from "@app/types/book-time-frame.type";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Images } from "@assets/images";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";
import { addToast } from "@app/components/toast/toast.service";
import useForceUpdate from "@core/hooks/use-force-update.hook";

const columnConfig: ColumnConfig[] = [
  {
    label: "Khung giờ bắt đầu",
    dataKey: "startTime",
    width: "30%",
    hasOrder: true,
  },
  {
    label: "Khung giờ kết thúc",
    dataKey: "endTime",
    width: "35%",
    hasOrder: true,
  },
  {
    label: "Id Sách",
    dataKey: "bookIds",
    width: "35%",
    hasOrder: true,
  },
];

function BookTimeManagement() {
  const [bookTimeFrames, setBookTimeFrames] = useState<BookTimeFrame[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [update, forceUpdate] = useForceUpdate();

  const navigate = useNavigate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    BookTimeFrameService.getList(requestParams, abortController.signal).then(
      (res) => {
        setBookTimeFrames(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    navigate(
      `/${RoutePath.BOOK_TIME_MANAGEMENT}/${RoutePath.CREATE_BOOK_TIME}`
    );
  };

  const handleEditClick = (bookTimeFrame: BookTimeFrame) => {
    navigate(
      `/${RoutePath.BOOK_TIME_MANAGEMENT}/${RoutePath.UPDATE_BOOK_TIME}/${bookTimeFrame.id}`
    );
  };

  const handleDeleteClick = (bookTimeFrameId: number) => {
    if (bookTimeFrameId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BOOK_TIME_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BookTimeFrameService.delete(bookTimeFrameId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Quản lý cài đặt khung giờ</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách cài đặt khung giờ"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={bookTimeFrames}
            meta={meta}
            hasAction={true}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BookTimeManagement;
